import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Spinner, Alert, Form, Row, Col, Modal } from 'react-bootstrap';
import { FiArrowLeft, FiEdit, FiSave, FiUser, FiPhone, FiMapPin, FiCreditCard, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { fetchClientByIdRequest, updateClientRequest } from '../../store/actions/clientActions';
import ManualReviewPolicies from './clientEdit/ManualReviewPolicies';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminClient.css';

const EMPTY_FORM = {
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    client_address: '',
    city: '',
    zip_code: '',
    country: '',
    country_code: '',
    date_joined: '',
    role: 'Client',
    status: '',
    birth_date: '',
    place_of_birth: '',
    country_of_birth: '',
    title: null,
    iban: '',
    bic: '',
    account_holder_name: '',
};

// Convertit différents formats de date en YYYY-MM-DD (input HTML5 date)
const convertToDateInput = (dateStr) => {
    if (!dateStr) return '';
    const trimmed = String(dateStr).trim();
    if (!trimmed) return '';

    const isoMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];

    const partsSlash = trimmed.split('/');
    if (partsSlash.length === 3) {
        const day = partsSlash[0].padStart(2, '0');
        const month = partsSlash[1].padStart(2, '0');
        const year = partsSlash[2].trim();
        if (day && month && year && year.length === 4) {
            return `${year}-${month}-${day}`;
        }
    }
    return '';
};

// YYYY-MM-DD (input HTML5 date) → DD/MM/YYYY (format API)
const convertToApiFormat = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return '';
};

const clientToForm = (client) => ({
    first_name: client.first_name || '',
    last_name: client.last_name || '',
    email: client.email || '',
    phone_number: client.phone_number || client.phone || '',
    client_address: client.client_address || client.adress || client.user_address || client.address || '',
    city: client.city || '',
    zip_code: client.zip_code || '',
    country: client.country || '',
    country_code: client.country_code || '',
    date_joined: client.date_joined ? convertToDateInput(client.date_joined) : '',
    role: client.role || 'Client',
    status: client.status || '',
    birth_date: client.birth_date ? convertToDateInput(client.birth_date) : '',
    place_of_birth: client.place_of_birth || '',
    country_of_birth: client.country_of_birth || '',
    title: client.title || '',
    iban: client.iban || '',
    bic: client.bic || '',
    account_holder_name: client.account_holder_name || '',
});

const sanitizeOptionalField = (value) => {
    if (value === null || value === undefined) return null;
    const trimmed = String(value).trim();
    return trimmed === '' ? null : trimmed;
};

const SectionTitle = ({ icon: Icon, children }) => (
    <h5 className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
        <Icon aria-hidden="true" />
        {children}
    </h5>
);

const AdminClientEdit = () => {
    const { clientId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { selectedClient, update } = useSelector((state) => state.clients);

    const [formData, setFormData] = useState(EMPTY_FORM);
    const [formError, setFormError] = useState(null);
    const [prefilled, setPrefilled] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);
    const requestedRef = useRef(false);

    useEffect(() => {
        if (!clientId) return;
        requestedRef.current = false;
        setPrefilled(false);
        dispatch(fetchClientByIdRequest(clientId));
    }, [clientId, dispatch]);

    useEffect(() => {
        if (selectedClient.loading) {
            requestedRef.current = true;
            return;
        }
        if (!requestedRef.current || prefilled) return;
        const client = selectedClient?.data?.client || selectedClient?.data;
        if (client && typeof client === 'object') {
            setFormData(clientToForm(client));
            setPrefilled(true);
        }
    }, [selectedClient, prefilled]);

    const showSuccess = submitted && update.success && !update.loading;
    const manualReviewPolicies = Array.isArray(update.policiesRequiringManualReview)
        ? update.policiesRequiringManualReview
        : [];

    useEffect(() => {
        if (showSuccess) {
            setShowSuccessModal(true);
            if (manualReviewPolicies.length > 0) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showSuccess]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value === '' && name === 'title' ? null : value,
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormError(null);

        const accountHolderName = sanitizeOptionalField(formData.account_holder_name);
        if (accountHolderName != null && accountHolderName.length < 2) {
            setFormError('Le titulaire du compte doit contenir au moins 2 caractères.');
            return;
        }
        if (accountHolderName != null && accountHolderName.length > 255) {
            setFormError('Le titulaire du compte ne peut pas dépasser 255 caractères.');
            return;
        }

        let birthDateFormatted = null;
        if (formData.birth_date && formData.birth_date.trim() !== '') {
            const converted = convertToApiFormat(formData.birth_date);
            birthDateFormatted = converted !== '' ? converted : null;
        }

        const dataToSend = {
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            email: formData.email.trim(),
            city: sanitizeOptionalField(formData.city),
            client_address: sanitizeOptionalField(formData.client_address),
            country: sanitizeOptionalField(formData.country),
            zip_code: sanitizeOptionalField(formData.zip_code),
            phone_number: sanitizeOptionalField(formData.phone_number),
            country_code: sanitizeOptionalField(formData.country_code),
            iban: sanitizeOptionalField(formData.iban),
            bic: sanitizeOptionalField(formData.bic),
            account_holder_name: accountHolderName,
            birth_date: birthDateFormatted,
            place_of_birth: sanitizeOptionalField(formData.place_of_birth),
            country_of_birth: sanitizeOptionalField(formData.country_of_birth),
            title: formData.title === '' || formData.title == null ? null : formData.title,
        };
        setPendingPayload(dataToSend);
    };

    const handleConfirmSave = () => {
        if (!pendingPayload) return;
        setSubmitted(true);
        dispatch(updateClientRequest(clientId, pendingPayload));
        setPendingPayload(null);
    };

    const goBack = () => navigate('/admin/client');
    const disabled = update.loading;
    const clientName = `${formData.first_name} ${formData.last_name}`.trim();

    return (
        <div className="dashboard-page admin-client-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiEdit className="me-2" />
                                Modifier le client
                            </h2>
                            <p className="text-muted mb-0">
                                {clientName || 'Mettez à jour les informations du client'}
                            </p>
                        </div>
                        <Button variant="outline-secondary" onClick={goBack}>
                            <FiArrowLeft className="me-2" /> Retour
                        </Button>
                    </div>

                    {selectedClient.loading && !prefilled ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement du client...</p>
                        </div>
                    ) : selectedClient.error && !prefilled ? (
                        <Alert variant="danger" className="mt-3">{selectedClient.error}</Alert>
                    ) : (
                        <Form onSubmit={handleSubmit} className="mt-3">
                            {submitted && update.error && !update.loading && (
                                <Alert variant="danger">{update.error}</Alert>
                            )}
                            {formError && <Alert variant="danger">{formError}</Alert>}
                            {showSuccess && manualReviewPolicies.length > 0 && (
                                <ManualReviewPolicies
                                    policies={manualReviewPolicies}
                                    onDismiss={() => setSubmitted(false)}
                                />
                            )}

                            <Card className="mb-3">
                                <Card.Body>
                                    <SectionTitle icon={FiUser}>Identité</SectionTitle>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Group className="mb-3" controlId="client-title">
                                                <Form.Label>Titre</Form.Label>
                                                <Form.Select
                                                    name="title"
                                                    value={formData.title ?? ''}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                >
                                                    <option value="">Sélectionner...</option>
                                                    <option value="Monsieur">Monsieur</option>
                                                    <option value="Madame">Madame</option>
                                                    <option value="Je préfère ne pas dire">Je préfère ne pas dire</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3" controlId="client-first-name">
                                                <Form.Label>Prénom *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="first_name"
                                                    value={formData.first_name}
                                                    onChange={handleInputChange}
                                                    required
                                                    minLength={2}
                                                    maxLength={100}
                                                    disabled={disabled}
                                                />
                                                <Form.Text className="text-muted">2 à 100 caractères requis</Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3" controlId="client-last-name">
                                                <Form.Label>Nom *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="last_name"
                                                    value={formData.last_name}
                                                    onChange={handleInputChange}
                                                    required
                                                    minLength={2}
                                                    maxLength={100}
                                                    disabled={disabled}
                                                />
                                                <Form.Text className="text-muted">2 à 100 caractères requis</Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3" controlId="client-birth-date">
                                                <Form.Label>Date de naissance</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="birth_date"
                                                    value={formData.birth_date}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3" controlId="client-place-of-birth">
                                                <Form.Label>Lieu de naissance</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="place_of_birth"
                                                    value={formData.place_of_birth}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3" controlId="client-country-of-birth">
                                                <Form.Label>Pays de naissance</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="country_of_birth"
                                                    value={formData.country_of_birth}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            <Card className="mb-3">
                                <Card.Body>
                                    <SectionTitle icon={FiPhone}>Contact et compte</SectionTitle>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="client-email">
                                                <Form.Label>Email *</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    required
                                                    disabled={disabled}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3" controlId="client-country-code">
                                                <Form.Label>Code pays</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="country_code"
                                                    value={formData.country_code}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                    placeholder="Ex: +33, FR"
                                                    maxLength={10}
                                                />
                                                <Form.Text className="text-muted">Max 10 caractères</Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3" controlId="client-phone">
                                                <Form.Label>Téléphone</Form.Label>
                                                <Form.Control
                                                    type="tel"
                                                    name="phone_number"
                                                    value={formData.phone_number}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3" controlId="client-date-joined">
                                                <Form.Label>Date d'inscription</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="date_joined"
                                                    value={formData.date_joined}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3" controlId="client-role">
                                                <Form.Label>Rôle</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="role"
                                                    value={formData.role}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3" controlId="client-status">
                                                <Form.Label>Statut</Form.Label>
                                                <Form.Select
                                                    name="status"
                                                    value={formData.status}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                >
                                                    <option value="">Sélectionner...</option>
                                                    <option value="Active">Active</option>
                                                    <option value="Inactive">Inactive</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            <Card className="mb-3">
                                <Card.Body>
                                    <SectionTitle icon={FiMapPin}>Adresse</SectionTitle>
                                    <Form.Group className="mb-3" controlId="client-address">
                                        <Form.Label>Adresse</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_address"
                                            value={formData.client_address}
                                            onChange={handleInputChange}
                                            disabled={disabled}
                                        />
                                    </Form.Group>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="client-city">
                                                <Form.Label>Ville</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3" controlId="client-zip">
                                                <Form.Label>Code postal</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="zip_code"
                                                    value={formData.zip_code}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3" controlId="client-country">
                                                <Form.Label>Pays</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="country"
                                                    value={formData.country}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            <Card className="mb-3">
                                <Card.Body>
                                    <SectionTitle icon={FiCreditCard}>Informations bancaires</SectionTitle>
                                    <Form.Group className="mb-3" controlId="client-account-holder">
                                        <Form.Label>Titulaire du compte</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="account_holder_name"
                                            value={formData.account_holder_name}
                                            onChange={handleInputChange}
                                            disabled={disabled}
                                            placeholder="Nom du titulaire du compte bancaire"
                                            minLength={2}
                                            maxLength={255}
                                        />
                                        <Form.Text className="text-muted">Optionnel — 2 à 255 caractères</Form.Text>
                                    </Form.Group>
                                    <Row>
                                        <Col md={8}>
                                            <Form.Group className="mb-3" controlId="client-iban">
                                                <Form.Label>IBAN</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="iban"
                                                    value={formData.iban}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                    placeholder="International Bank Account Number"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3" controlId="client-bic">
                                                <Form.Label>BIC</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="bic"
                                                    value={formData.bic}
                                                    onChange={handleInputChange}
                                                    disabled={disabled}
                                                    placeholder="Bank Identifier Code"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            <div className="d-flex justify-content-end gap-2 pt-2 border-top">
                                <Button variant="secondary" onClick={goBack} disabled={disabled}>
                                    Annuler
                                </Button>
                                <Button variant="primary" type="submit" disabled={disabled}>
                                    {disabled ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Enregistrement...
                                        </>
                                    ) : (
                                        <>
                                            <FiSave className="me-2" />
                                            Enregistrer
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Card.Body>
            </Card>

            <Modal
                show={Boolean(pendingPayload)}
                onHide={() => setPendingPayload(null)}
                centered
                aria-labelledby="client-update-confirm-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="client-update-confirm-title">Confirmer l’enregistrement</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">
                        Vous êtes sur le point de modifier les informations du client{' '}
                        <strong>{clientName || '—'}</strong>
                        {formData.email ? <> ({formData.email})</> : null}.
                    </p>
                    <p className="text-muted small mb-0 d-flex align-items-start gap-2">
                        <FiAlertTriangle className="text-warning mt-1 flex-shrink-0" aria-hidden="true" />
                        <span>
                            Cette modification peut nécessiter une vérification manuelle des contrats associés.
                            Êtes-vous sûr de vouloir continuer ?
                        </span>
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setPendingPayload(null)}>
                        Annuler
                    </Button>
                    <Button variant="primary" autoFocus onClick={handleConfirmSave}>
                        <FiSave className="me-2" aria-hidden="true" />
                        Confirmer l’enregistrement
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showSuccessModal}
                onHide={() => setShowSuccessModal(false)}
                centered
                aria-labelledby="client-update-success-title"
            >
                <Modal.Body className="text-center p-4">
                    <FiCheckCircle size={48} className="text-success mb-3" aria-hidden="true" />
                    <h5 id="client-update-success-title" className="mb-2">
                        Le client a été mis à jour avec succès.
                    </h5>
                    {manualReviewPolicies.length > 0 && (
                        <p className="text-muted mb-0 d-flex align-items-start justify-content-center gap-2">
                            <FiAlertTriangle className="text-warning mt-1 flex-shrink-0" aria-hidden="true" />
                            <span>
                                Une vérification manuelle est requise pour {manualReviewPolicies.length}{' '}
                                contrat{manualReviewPolicies.length > 1 ? 's' : ''}.
                            </span>
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-center border-0 pt-0 pb-4">
                    <Button variant="primary" autoFocus onClick={() => setShowSuccessModal(false)} style={{ minWidth: 160 }}>
                        {manualReviewPolicies.length > 0 ? 'Voir les contrats' : 'Fermer'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminClientEdit;
